import { ErrorBoundary, Illustration, Table, Text, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, FlatList } from 'native-base';
import React from 'react';
import { Dimensions, DimensionValue } from 'react-native';

import {
  Transaction,
  TransactionFlowHistoricalSearch,
} from '../../../../../store/transactionFlow/types';
import { useTableSort } from '../../../../TransactionFlowDetails/components/AssetTable/_hooks/useTableSort';

import { TransactionTableMobile } from './components/Mobile';
import { TransactionTableTablet } from './components/Tablet';
import { TransactionHeader } from './components/TransactionHeader';
import { TransactionItem } from './components/TransactionItem';
import { fieldsMobileTablet, tableHeaders } from './constants';

interface TransactionTableProps {
  data: TransactionFlowHistoricalSearch | undefined;
  error: boolean;
  isMobile?: boolean;
  loading: boolean;
  onErrorRefresh: () => void;
  onSelectAccount?: (cpfCnpj: string) => void;
}

export const TransactionTable: React.FC<TransactionTableProps> = ({
  error,
  onErrorRefresh,
  data,
  loading,
  onSelectAccount,
}) => {
  const maxHeight = `${Dimensions.get('window').height - 295}px` as DimensionValue;
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const { sortedData, sortColumn, sortDirection, handleSort } = useTableSort({
    initialData: data?.transaction ?? [],
  });

  if (data?.transaction.length === 0 && !loading) {
    return (
      <VStack alignItems="center" paddingY={Spacing['stack.lg']}>
        <VStack alignItems="center" space={Spacing['stack.quarck']}>
          <Illustration name="NotFoundIllustration" testId="empty-illustration" />
          <Text color="neutral.black" variant="semibold">
            Nenhum resultado corresponde à sua pesquisa
          </Text>
          <Text color="neutral.black">
            Por favor, verifique o campo informado e tente novamente.
          </Text>
        </VStack>
      </VStack>
    );
  }

  if (error) {
    return <ErrorBoundary onRefresh={onErrorRefresh} />;
  }

  if (isMobile) {
    return (
      <TransactionTableMobile
        data={sortedData as Transaction[]}
        fields={fieldsMobileTablet}
        loading={loading}
        onSelectClient={onSelectAccount}
      />
    );
  }

  if (isTablet) {
    return (
      <TransactionTableTablet
        data={sortedData as Transaction[]}
        fields={fieldsMobileTablet}
        loading={loading}
        onSelectClient={onSelectAccount}
      />
    );
  }

  const ItemSeparator = () => <Box height={Spacing['stack.nano']} />;

  return (
    <Box overflow={'hidden'}>
      <Table isLoading={loading} cols={tableHeaders.length} rows={5}>
        <TransactionHeader
          tableHeadItems={tableHeaders}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
        />
        <FlatList
          flex={1}
          data={sortedData as Transaction[]}
          windowSize={20}
          maxToRenderPerBatch={20}
          contentContainerStyle={{ maxHeight }}
          updateCellsBatchingPeriod={5}
          keyExtractor={(item) => item.movementId.toString()}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={ItemSeparator}
          renderItem={({ item }) => (
            <TransactionItem
              key={item.movementId}
              transaction={item}
              onSelectClient={onSelectAccount}
            />
          )}
        />
      </Table>
    </Box>
  );
};
